import { useEffect, useState } from "react";
import { Ticket, RefreshCw, Download, Users, Send, CheckCircle2, Wand2, Power, Search, Bell, Megaphone, Database } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 })}`;
const fmt = (s) => (s ? new Date(s).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

export default function AdminCouponsPanel({ api }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [q, setQ] = useState("");
  const [report, setReport] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [status, setStatus] = useState("active");
  const [view, setView] = useState("merchants"); // merchants | coupons | campaigns

  const load = async () => {
    try {
      const [r, l] = await Promise.all([
        api().get(`/coupons/report?month=${month}&q=${encodeURIComponent(q)}`),
        api().get(`/coupons/list?status=${status}&q=${encodeURIComponent(q)}`),
      ]);
      setReport(r.data); setCoupons(l.data.coupons);
    } catch { toast.error("No pude cargar el reporte de cupones"); }
  };
  useEffect(() => { load(); }, [month, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (c) => {
    let reason = "";
    if (c.active) {
      reason = window.prompt(`Desactivar el cupón "${c.title}" de ${c.business_name}. Motivo (opcional):`) ?? null;
      if (reason === null) return;
    }
    try {
      await api().post(`/coupons/${c.coupon_id}/toggle`, { active: !c.active, reason });
      toast.success(c.active ? "Cupón desactivado — los usuarios lo verán como vencido" : "Cupón reactivado");
      load();
    } catch { toast.error("No pude cambiar el estado"); }
  };

  if (!report) return <div className="text-sm text-neutral-500">Cargando cupones…</div>;

  return (
    <div className="space-y-6" data-testid="admin-coupons-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-extrabold flex items-center gap-2"><Ticket className="w-6 h-6 text-[#FF007A]" /> Cupones & Cuponeras</h2>
          <p className="text-xs text-neutral-500 mt-1">Control global del sistema de cupones: bases de datos por comercio, campañas, canjes, costos IA y moderación.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-full border border-neutral-300 px-3 text-xs" data-testid="admin-coupons-month" />
          <Button onClick={() => exportCSV(report, month)} variant="outline" size="sm" className="rounded-full" data-testid="admin-coupons-csv"><Download className="w-3.5 h-3.5" /> CSV</Button>
          <Button onClick={load} variant="outline" size="sm" className="rounded-full" data-testid="admin-coupons-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </div>

      <Kpis report={report} />

      <div className="flex flex-wrap items-center gap-2">
        {[["merchants", "Por comercio"], ["coupons", "Cupones (moderación)"], ["campaigns", "Últimas campañas"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`admin-coupons-view-${k}`} className={`px-4 py-2 rounded-full text-xs font-bold ${view === k ? "bg-black text-white" : "bg-white border border-neutral-200 text-neutral-600"}`}>{l}</button>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <div className="relative"><Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar comercio / cupón" className="h-9 pl-8 rounded-full text-xs w-56" data-testid="admin-coupons-search" /></div>
          {view === "coupons" && (
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-full border border-neutral-300 px-3 text-xs" data-testid="admin-coupons-status">
              <option value="active">Activos</option><option value="inactive">Inactivos</option><option value="admin_disabled">Desactivados por admin</option><option value="all">Todos</option>
            </select>
          )}
        </div>
      </div>

      {view === "merchants" && <MerchantsTable rows={report.by_merchant} />}
      {view === "coupons" && <CouponsGrid coupons={coupons} onToggle={toggle} />}
      {view === "campaigns" && <CampaignsTable rows={report.recent_campaigns} />}
    </div>
  );
}

function exportCSV(report, month) {
  const head = ["Comercio", "Telefono", "Categoria", "Base clientes", "Cupones", "Activos", "Bienvenida", "Campañas", "Push", "Enviados", "Vistos", "Canjeados", "Conversion %", "Descuento MXN", "Venta MXN", "Diseños IA mes", "Costo IA MXN"];
  const rows = report.by_merchant.map((r) => [r.business_name, r.phone, r.category, r.subscribers, r.coupons_total, r.coupons_active, r.coupons_welcome, r.campaigns, r.push_sent, r.sent, r.viewed, r.redeemed, r.conversion, r.discount_total, r.ticket_total, r.ai_designs_month, r.ai_cost_mxn_month]);
  const csv = [head, ...rows].map((r) => r.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  a.download = `cupones-${month}.csv`; a.click();
}

function Kpis({ report }) {
  const t = report.totals;
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <K icon={Database} label="Comercios usando cupones" value={t.merchants_using} sub={`${t.coupons_active} cupones activos de ${t.coupons_total}`} color="bg-[#0055FF]" testid="admin-k-merchants" />
      <K icon={Users} label="Usuarios en cuponeras" value={t.unique_users} sub={`${t.subscriptions} suscripciones · ${t.users_named} con nombre`} color="bg-[#FF007A]" testid="admin-k-users" />
      <K icon={Send} label="Cupones entregados" value={t.sent} sub={`${t.viewed} vistos · ${t.campaigns} campañas`} color="bg-neutral-900" testid="admin-k-sent" />
      <K icon={CheckCircle2} label="Canjeados" value={t.redeemed} sub={`${t.conversion}% conversión`} color="bg-emerald-600" testid="admin-k-redeemed" />
      <K icon={Megaphone} label="Venta generada por cupones" value={money(t.ticket_total)} sub={`${money(t.discount_total)} otorgados en descuentos`} color="bg-amber-500" testid="admin-k-sales" />
      <K icon={Bell} label="Celulares con push activo" value={t.push_devices} sub={t.unique_users ? `${Math.round((t.push_devices * 100) / t.unique_users)}% de los usuarios` : "—"} color="bg-violet-600" testid="admin-k-push" />
      <K icon={Wand2} label={`Diseños IA cupones (${report.month})`} value={t.ai_designs_month} sub={`Costo ${money(t.ai_cost_mxn_month)} MXN · $0.04 USD c/u`} color="bg-pink-600" testid="admin-k-ai" />
      <K icon={Wand2} label="Diseños IA histórico" value={t.ai_designs_all_time} sub={`Costo acumulado ${money(t.ai_cost_mxn_all_time)} MXN`} color="bg-neutral-700" testid="admin-k-ai-all" />
    </div>
  );
}

function MerchantsTable({ rows }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-coupons-merchants-table">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 bg-neutral-50">
          <th className="p-3 font-bold">Comercio</th><th className="p-3 font-bold text-right">Base</th><th className="p-3 font-bold text-right">Cupones</th><th className="p-3 font-bold text-right">Campañas</th><th className="p-3 font-bold text-right">Push</th><th className="p-3 font-bold text-right">Enviados</th><th className="p-3 font-bold text-right">Canjeados</th><th className="p-3 font-bold text-right">Conv.</th><th className="p-3 font-bold text-right">Venta</th><th className="p-3 font-bold text-right">Descuentos</th><th className="p-3 font-bold text-right">IA mes</th>
        </tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.merchant_id} className="border-b border-neutral-100" data-testid={`admin-coupons-row-${r.merchant_id}`}>
              <td className="p-3"><div className="font-bold">{r.business_name}</div><div className="text-[11px] text-neutral-500">{r.phone} · {r.category}</div></td>
              <td className="p-3 text-right font-bold text-[#0055FF]">{r.subscribers}</td>
              <td className="p-3 text-right">{r.coupons_active}/{r.coupons_total}{r.coupons_welcome ? <span className="ml-1 text-[9px] rounded-full bg-emerald-100 text-emerald-700 px-1.5 font-bold">{r.coupons_welcome} bienv.</span> : null}</td>
              <td className="p-3 text-right">{r.campaigns}</td><td className="p-3 text-right">{r.push_sent}</td><td className="p-3 text-right">{r.sent}</td>
              <td className="p-3 text-right font-bold text-emerald-600">{r.redeemed}</td><td className="p-3 text-right">{r.conversion}%</td>
              <td className="p-3 text-right">{money(r.ticket_total)}</td><td className="p-3 text-right text-neutral-500">{money(r.discount_total)}</td>
              <td className="p-3 text-right">{r.ai_designs_month}/6 <span className="text-[10px] text-neutral-400">{money(r.ai_cost_mxn_month)}</span></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={11} className="text-center py-8 text-neutral-500">Ningún comercio ha usado cupones todavía.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function CouponsGrid({ coupons, onToggle }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-coupons-list">
      {coupons.map((c) => (
        <div key={c.coupon_id} className={`rounded-2xl border bg-white p-3 flex gap-3 ${c.admin_disabled ? "border-red-300 bg-red-50/40" : "border-neutral-200"}`} data-testid={`admin-coupon-${c.coupon_id}`}>
          <div className="w-16 h-20 rounded-xl overflow-hidden bg-gradient-to-br from-[#FF007A] to-[#0055FF] shrink-0 grid place-items-center text-white text-[10px] font-bold">{c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : c.discount_label}</div>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-extrabold text-sm leading-tight truncate">{c.discount_label} · {c.title}</div>
            <div className="text-[11px] text-neutral-500 truncate">{c.business_name}</div>
            <div className="text-[10px] text-neutral-500 mt-1">{c.sent} enviados · {c.redeemed} canjeados{c.valid_until ? ` · vence ${c.valid_until}` : ""}</div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {c.welcome && <Tag tone="emerald">Bienvenida</Tag>}
              {c.has_ai && <Tag tone="pink">IA</Tag>}
              {c.expired && <Tag tone="neutral">Vencido</Tag>}
              {c.admin_disabled && <Tag tone="red">Desactivado admin{c.admin_reason ? `: ${c.admin_reason}` : ""}</Tag>}
              {!c.active && !c.admin_disabled && <Tag tone="neutral">Inactivo</Tag>}
            </div>
            <button onClick={() => onToggle(c)} data-testid={`admin-coupon-toggle-${c.coupon_id}`} className={`mt-2 inline-flex items-center gap-1 text-[11px] font-bold rounded-full px-2.5 py-1 ${c.active ? "bg-red-100 text-red-700 hover:bg-red-200" : "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"}`}>
              <Power className="w-3 h-3" /> {c.active ? "Desactivar" : "Reactivar"}
            </button>
          </div>
        </div>
      ))}
      {coupons.length === 0 && <div className="col-span-full text-center py-8 text-sm text-neutral-500">Sin cupones con ese filtro.</div>}
    </div>
  );
}

function CampaignsTable({ rows }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-x-auto">
      <table className="w-full text-sm" data-testid="admin-coupons-campaigns-table">
        <thead><tr className="text-left text-[10px] uppercase tracking-widest text-neutral-500 border-b border-neutral-200 bg-neutral-50">
          <th className="p-3 font-bold">Fecha</th><th className="p-3 font-bold">Comercio</th><th className="p-3 font-bold">Campaña</th><th className="p-3 font-bold">Cupón</th><th className="p-3 font-bold">Segmento</th><th className="p-3 font-bold text-right">Audiencia</th><th className="p-3 font-bold text-right">Enviados</th><th className="p-3 font-bold text-right">Push</th>
        </tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.campaign_id} className="border-b border-neutral-100">
              <td className="p-3 text-xs">{fmt(c.created_at)}</td><td className="p-3 font-bold">{c.business_name}</td><td className="p-3">{c.name}</td><td className="p-3 text-xs">{c.coupon_title}</td>
              <td className="p-3 text-xs">{c.segment}</td><td className="p-3 text-right">{c.audience_size}</td><td className="p-3 text-right">{c.sent_count}</td><td className="p-3 text-right">{c.push_sent ?? 0}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={8} className="text-center py-8 text-neutral-500">Aún no hay campañas.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function K({ icon: Icon, label, value, sub, color, testid }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-4 flex gap-3" data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${color} text-white grid place-items-center shrink-0`}><Icon className="w-5 h-5" /></div>
      <div className="min-w-0"><div className="font-heading font-extrabold text-2xl leading-none">{value}</div><div className="text-[11px] font-bold text-neutral-700 mt-1 leading-tight">{label}</div><div className="text-[10px] text-neutral-500 leading-tight">{sub}</div></div>
    </div>
  );
}

function Tag({ tone, children }) {
  const cls = { emerald: "bg-emerald-100 text-emerald-700", pink: "bg-pink-100 text-pink-700", red: "bg-red-100 text-red-700", neutral: "bg-neutral-200 text-neutral-600" }[tone];
  return <span className={`text-[9px] font-bold rounded-full px-1.5 py-0.5 ${cls}`}>{children}</span>;
}
